/**
 * PathOrbit — the /discovery Paths tab hero visual: a ring of circular path
 * artwork. One path anchors the centre at a larger size; the rest orbit it
 * clockwise starting at twelve o'clock, in the order the rows below them are
 * listed, so the visual and the list read as one thing.
 *
 * State is carried by treatment, never by a different image: a progress ring
 * around an in-flight path, a check badge on a finished one (`complete`), a
 * padlock badge on one the viewer cannot work yet (`locked`), full colour in
 * the centre, a slight mute on every other available path, and near-grey for
 * not-built paths (`dimmed`). The overlays themselves come from
 * `pathDisc.discOverlaysHtml`, shared with the path rows below the hero, so a
 * disc and its row tile can never disagree; the tiers are CSS tokens. This
 * file only classes the discs.
 *
 * This is `PolarProgressChart`'s sibling and deliberately mirrors its
 * conventions — the constructor/build/destroy shape, the `_hoverCapable`
 * pointer gate, the reduced-motion entrance gate, and the spotlight contract
 * that keeps the row↔visual cross-highlight from echoing.
 *
 *   import PathOrbit from '@/components/PathOrbit.js';
 *   const orbit = new PathOrbit('[data-path-orbit]', {
 *     paths: [{ id: 'foundation-2', name: '…', imageUrl: '…', percent: 40, dimmed: false, locked: false }],
 *     centerPath: { id: 'foundation', name: '…', imageUrl: '…', percent: 100, complete: true },
 *     onHover: (pathId) => { … sync row hot state … },
 *     onClick: (pathId) => { … open the path drawer … }
 *   });
 *
 *   orbit.spotlight('foundation-2');  // dim every other disc, highlight this one
 *   orbit.spotlight(null);            // clear
 *   orbit.destroy();
 *
 * ACCESSIBILITY: the whole orbit is `aria-hidden="true"` and carries no
 * per-satellite `role`/`tabindex`/`aria-label`. This matches the polar chart
 * (`PolarProgressChart.js:22-23`) for the same reason: the accessible
 * affordance lives in the path rows below the hero, which are real
 * `role="button" tabindex="0"` surfaces naming the same paths. Adding ARIA
 * here would duplicate every path in the a11y tree, not add a path to it.
 *
 * GEOMETRY lives in CSS custom properties on `.path-orbit`
 * (`css/components/path-orbit.css`) so radius and disc sizing are tunable
 * without touching this file. JS supplies only the per-satellite angle
 * (`--orbit-angle`) and the crowding scale (`--orbit-satellite-scale`).
 */

import { discOverlaysHtml } from './pathDisc.js';

// Crowding clamp. The satellite size token in CSS is tuned so SIX satellites
// sit shoulder to shoulder with a comfortable gap — that is the design target
// (six paths + one centre). Past six, satellites shrink on the exact
// non-overlap curve below.
export const UNCROWDED_SATELLITES = 6;
// Floor. Without one, a large `n` would shrink the artwork to an illegible
// speck; with one, discs past n=14 may just touch. Touching beats vanishing.
export const MIN_SATELLITE_SCALE = 0.42;

// Entrance: the centre pops in first, the satellites follow clockwise on a
// stagger, and an in-flight ring sweeps to its percent once its disc has
// landed. JS contributes only `--orbit-delay` per disc (pathDisc writes
// `--disc-ring-drawn` for the sweep); the keyframes, durations and the spring
// live in CSS.
//
// The motion itself is a CSS keyframe carrying `--orbit-delay`, not a
// class-release transition like the polar chart's: staggering a transition
// requires the delay to live in the RESTING rule, where it would then also
// delay every later spotlight repaint. The gate below is the chart's, exactly.
const ENTER_BASE_DELAY_MS = 90;
const ENTER_STAGGER_MS = 70;

/**
 * Angle of the i-th satellite, in degrees clockwise from twelve o'clock.
 * Consumed as a CSS `rotate()` on the satellite's arm, where a positive angle
 * is clockwise — so this number is the visual angle with no sign juggling.
 * @param {number} i - Satellite index (0-based)
 * @param {number} n - Satellite count
 * @returns {number} Degrees, 0 = twelve o'clock
 */
export function satelliteAngle(i, n) {
  if (!Number.isFinite(n) || n <= 0) return 0;
  return (360 / n) * i;
}

/**
 * Crowding scale for `n` satellites — the multiplier CSS applies to the
 * satellite diameter token so discs never overlap as the ring fills up.
 *
 * Adjacent satellite centres are a chord `2R·sin(pi/n)` apart, so the largest
 * non-overlapping diameter is proportional to `sin(pi/n)`. Normalising against
 * the six-satellite design target gives `sin(pi/n) / sin(pi/6)`: exactly 1 at
 * the target, shrinking on the true geometry beyond it. `MIN_SATELLITE_SCALE`
 * floors it.
 *
 * Deliberately one ring that degrades, not a second ring or a "+N" collapse —
 * `BETA_PATH_GATE=off` (the normal developer configuration) lists eleven
 * paths, so this path is walked on the first local run.
 *
 * @param {number} n - Satellite count
 * @returns {number} Scale multiplier in [MIN_SATELLITE_SCALE, 1]
 */
export function satelliteScale(n) {
  if (!Number.isFinite(n) || n <= UNCROWDED_SATELLITES) return 1;
  const exact = Math.sin(Math.PI / n) / Math.sin(Math.PI / UNCROWDED_SATELLITES);
  return Math.max(MIN_SATELLITE_SCALE, Math.min(1, Number(exact.toFixed(4))));
}

export class PathOrbit {
  /**
   * @param {string|Element} mountEl - Selector or element to mount the orbit into
   * @param {Object} [opts] - Options
   * @param {Array<{id: string, name?: string, imageUrl?: string, percent?: number|null, dimmed?: boolean, complete?: boolean, locked?: boolean}>} [opts.paths=[]] -
   *   Satellites in row order, EXCLUDING the centre. Laid out clockwise from
   *   twelve o'clock. A non-array (or omitted) value renders the centre alone
   *   rather than throwing — the host's contract is that a visual which cannot
   *   mount must still leave the rows usable, so this component never turns a
   *   thin payload into an exception.
   *   Supported range: 0–14 satellites are guaranteed not to overlap by the
   *   shrink clamp alone, whatever the CSS size tokens are set to (the design
   *   target is 6). Past 14 the `MIN_SATELLITE_SCALE` floor takes over and the
   *   guarantee becomes a function of those tokens — with today's values
   *   neighbours still clear each other into the low twenties, and beyond that
   *   they touch rather than shrinking the artwork out of legibility.
   * @param {{id: string, name?: string, imageUrl?: string, percent?: number|null, dimmed?: boolean, complete?: boolean, locked?: boolean}|null} [opts.centerPath=null] -
   *   The path anchoring the middle at a larger size. Null renders the ring
   *   with an empty centre.
   * @param {(pathId: string|null) => void} [opts.onHover] - Fired on
   *   pointer-originated disc hover/unhover, hover-capable pointers only.
   *   Never fired by `spotlight()`.
   * @param {(pathId: string) => void} [opts.onClick] - Fired on disc
   *   click/tap, unconditionally (no hover gate), so tap-to-open works on the
   *   first tap.
   * @param {boolean} [opts.animate=true] - Play the staggered grow-in on
   *   mount. Skipped under a reduced-motion preference — and wherever
   *   `matchMedia` is unavailable, since motion that cannot be confirmed
   *   acceptable is not played.
   */
  constructor(mountEl, opts = {}) {
    this.mountEl = typeof mountEl === 'string' ? document.querySelector(mountEl) : mountEl;
    if (!this.mountEl) throw new Error('PathOrbit: mount element not found');

    this.opts = Object.assign(
      {
        paths: [],
        centerPath: null,
        onHover: null,
        onClick: null,
        animate: true
      },
      opts
    );
    // Fail-soft on the data, strict on the mount: a caller that hands us a
    // null/undefined/garbage `paths` still gets a centre disc.
    if (!Array.isArray(this.opts.paths)) this.opts.paths = [];

    // Entrance gate — mirrors PolarProgressChart: play motion only when the
    // environment can confirm it is acceptable.
    this._motionOK = (() => {
      try {
        return !matchMedia('(prefers-reduced-motion: reduce)').matches;
      } catch {
        return false;
      }
    })();

    // Pointer gate: bind disc hover only on hover-capable pointers. An
    // unguarded synthesized pointerenter on tap would repaint the orbit and
    // make iOS Safari swallow the first tap (the documented content-row touch
    // trap). Click stays bound unconditionally below so tap-to-open always
    // fires on the first tap.
    this._hoverCapable = (() => {
      try {
        return matchMedia('(hover: hover)').matches;
      } catch {
        return false;
      }
    })();

    /** @type {Map<string, HTMLElement>} */
    this._discEls = new Map();
    /** @type {Array<{el: EventTarget, type: string, fn: Function}>} */
    this._listeners = [];
    this._build();
  }

  // ---- public API -------------------------------------------------------

  /**
   * Highlight one disc (dimming the rest), or clear the spotlight. Used to
   * sync the orbit to an externally-hovered path row.
   *
   * Does NOT fire `onHover` — that callback only fires for pointer-originated
   * disc hover. This is what stops the row↔visual echo: the host wires
   * `row hover -> orbit.spotlight()` and `orbit disc hover -> onHover -> row
   * hot class`, and if `spotlight()` also fired `onHover` the two channels
   * would feed each other (same contract as
   * `PolarProgressChart.spotlight()`).
   *
   * This method only toggles classes; what they change is CSS's call, under
   * one constraint that is load-bearing, not a style choice: the host binds
   * row `pointerover` through `bindTopicRows`, which is NOT hover-gated, so on
   * a touch device the first tap on a row synthesises `pointerover` ->
   * `spotlight()`, and a layout-affecting change there reproduces the
   * documented iOS first-tap swallow. So the stylesheet serves the hot disc's
   * scale only under `(hover: hover)`, and never writes inline geometry here.
   *
   * @param {string|null} [pathId=null] - Path id to spotlight, or null to clear
   */
  spotlight(pathId = null) {
    this._applySpotlight(pathId);
  }

  /**
   * Tear down the orbit: remove every listener, drop the mount's children and
   * release references. Safe to call multiple times.
   */
  destroy() {
    if (!this.mountEl) return;
    this._listeners.forEach(({ el, type, fn }) => el.removeEventListener(type, fn));
    this._listeners = [];
    this._discEls.clear();
    this.mountEl.innerHTML = '';
    this.root = null;
    this.mountEl = null;
  }

  // ---- internals --------------------------------------------------------

  /**
   * Register a listener so `destroy()` can take it back off again.
   * @param {EventTarget} el
   * @param {string} type
   * @param {Function} fn
   * @private
   */
  _on(el, type, fn) {
    el.addEventListener(type, fn);
    this._listeners.push({ el, type, fn });
  }

  /**
   * @param {string|null} pathId
   * @private
   */
  _applySpotlight(pathId) {
    if (!this.root) return;
    const dimming = pathId != null;
    this.root.classList.toggle('path-orbit--dimming', dimming);
    this._discEls.forEach((el, id) => {
      el.classList.toggle('is-hot', dimming && id === pathId);
    });
  }

  /**
   * Build one circular disc: artwork (or a neutral fallback), plus the shared
   * state overlays — a progress ring when the path is genuinely part-way
   * through, a check badge when it is finished, a padlock when it is locked.
   * @param {Object} path - Path view model
   * @param {string} variant - 'center' | 'satellite'
   * @returns {HTMLElement}
   * @private
   */
  _buildDisc(path, variant) {
    const disc = document.createElement('div');
    disc.className = `path-orbit__disc path-orbit__disc--${variant}`;
    if (path.dimmed) disc.classList.add('path-orbit__disc--withheld');
    disc.setAttribute('data-path-id', String(path.id));
    if (path.name) disc.setAttribute('data-path-name', String(path.name));

    if (path.imageUrl) {
      const img = document.createElement('img');
      img.className = 'path-orbit__art';
      img.alt = '';
      // Same idiom as the path row's artwork (progressHero.renderPathRow): a
      // broken asset must leave a neutral tinted disc, not a broken-image
      // glyph. Set BEFORE `src` so a synchronously-failing (cached-404) load
      // still lands on the fallback, and as an attribute rather than a bound
      // listener so it needs no teardown.
      img.setAttribute('onerror', "this.classList.add('is-missing')");
      img.src = path.imageUrl;
      disc.appendChild(img);
    } else {
      const stub = document.createElement('span');
      stub.className = 'path-orbit__art is-missing';
      disc.appendChild(stub);
    }

    // Ring and rim badge come from the renderer the path rows share. A
    // finished path wears the check whatever else is true of it; a locked one
    // the padlock; an in-flight one the ring (0 and 100 draw none).
    disc.insertAdjacentHTML('beforeend', discOverlaysHtml({
      percent: path.percent,
      badge: path.complete ? 'check' : (path.locked ? 'lock' : null)
    }));

    this._discEls.set(String(path.id), disc);
    this._bindDisc(disc, String(path.id));
    return disc;
  }

  /**
   * Wire one disc's pointer channels.
   * @param {HTMLElement} disc
   * @param {string} id
   * @private
   */
  _bindDisc(disc, id) {
    if (this._hoverCapable) {
      this._on(disc, 'pointerenter', () => {
        this._applySpotlight(id);
        if (this.opts.onHover) this.opts.onHover(id);
      });
      this._on(disc, 'pointerleave', () => {
        this._applySpotlight(null);
        if (this.opts.onHover) this.opts.onHover(null);
      });
    }
    // Unconditional — see the pointer gate in the constructor.
    this._on(disc, 'click', () => {
      if (this.opts.onClick) this.opts.onClick(id);
    });
  }

  /** @private */
  _build() {
    const paths = this.opts.paths.filter((p) => p && p.id != null);
    const n = paths.length;
    const entering = this.opts.animate !== false && this._motionOK;

    const root = document.createElement('div');
    root.className = 'path-orbit';
    // See the ACCESSIBILITY note at the top of the file: the path rows below
    // the hero are the sole keyboard/screen-reader path into this data.
    root.setAttribute('aria-hidden', 'true');
    root.style.setProperty('--orbit-satellite-scale', String(satelliteScale(n)));

    this._discEls.clear();

    // Dashed guide ring — the orbit's own echo of the polar chart's dotted
    // guide rings. Drawn first so every disc sits above it. Hidden when there
    // is nothing orbiting.
    if (n > 0) {
      const ring = document.createElement('div');
      ring.className = 'path-orbit__ring';
      root.appendChild(ring);
    }

    // Centre first in DOM order so the satellites' arms (which span the whole
    // square) stack above it and keep their pointer surfaces reachable.
    if (this.opts.centerPath && this.opts.centerPath.id != null) {
      const center = this._buildDisc(this.opts.centerPath, 'center');
      if (entering) center.style.setProperty('--orbit-delay', '0ms');
      root.appendChild(center);
    }

    // Satellites. Each rides an "arm": a full-square, pointer-transparent
    // layer rotated to the satellite's angle, with the disc pinned at the top
    // and counter-rotated so the artwork stays upright. This keeps the entire
    // radius/size calculation in CSS percentages — JS contributes the angle
    // and nothing else.
    paths.forEach((p, i) => {
      const arm = document.createElement('div');
      arm.className = 'path-orbit__arm';
      arm.style.setProperty('--orbit-angle', `${satelliteAngle(i, n)}deg`);

      const disc = this._buildDisc(p, 'satellite');
      if (entering) {
        disc.style.setProperty('--orbit-delay', `${ENTER_BASE_DELAY_MS + i * ENTER_STAGGER_MS}ms`);
      }
      arm.appendChild(disc);
      root.appendChild(arm);
    });

    this.mountEl.innerHTML = '';
    // The class stays for the life of the node — the keyframe runs once and
    // then hands the discs back to the cascade (`backwards` fill, never
    // `forwards`), so nothing has to be un-set on a frame boundary.
    if (entering) root.classList.add('path-orbit--enter');
    this.mountEl.appendChild(root);
    this.root = root;
  }
}

export default PathOrbit;
